/* eslint-disable @typescript-eslint/naming-convention */
import React, { ReactNode, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { Breadcrumbs, BreadcrumbsItem, CardRow, PageActions, PageHeader, PageHeaderAction, PageHeaderContent, PageTitle } from 'components';
import { routeConstants } from 'routes';
import { selectCurrentEmployee, setCurrentEmployeeRequest } from 'store/App';
import { selectSiteTipAcceptancePaymentSets } from 'store/Collections';
import { selectSite, selectSiteAcceptanceBalance } from 'store/Site/selectors';
import { AcceptancePaymentSetStatus } from 'types';
import { formatDateForUILongFormat } from 'utils/date';
import { PayTipsTooltip } from './components/styles';

interface ITipAcceptanceLayoutProps {
  pageActions?: boolean;
  hasStats?: boolean;
  children: ReactNode;
}

interface IParams {
  payment_set_id: string;
}

const TipAcceptanceScreenLayout = ({ children, pageActions = false, hasStats = false }: ITipAcceptanceLayoutProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { payment_set_id } = useParams<IParams>();
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentSiteId = currentEmployee?.siteId || '';
  const currentSite = useSelector(selectSite());
  const acceptanceBalance = useSelector(selectSiteAcceptanceBalance());
  const paymentSets = useSelector(selectSiteTipAcceptancePaymentSets());
  const acceptancePaymentSet = useMemo(() => paymentSets?.data.find((item) => item.id === payment_set_id), [payment_set_id, paymentSets]);

  useEffect(() => {
    if (currentSiteId) {
      dispatch(setCurrentEmployeeRequest({ ...currentEmployee, siteId: currentSiteId }));
    }
  }, [currentSiteId]);

  const isTipsPage = location.pathname.endsWith('tips');

  return (
    <>
      <PageHeader hasStats={hasStats}>
        <Breadcrumbs>
          <BreadcrumbsItem label="Payments" to={routeConstants.private.paymentsOverview} />
          <BreadcrumbsItem label="Tip Acceptance" to={routeConstants.private.paymentsAcceptance} />
          {isTipsPage && <BreadcrumbsItem label="View Tips" />}
        </Breadcrumbs>

        <PageHeaderContent>
          <PageTitle title={currentSite?.name} subtitle={acceptancePaymentSet?.paymentDate ? formatDateForUILongFormat(acceptancePaymentSet.paymentDate, false, true) : null} />
          {pageActions && payment_set_id && (
            <PageActions>
              {currentSite && acceptancePaymentSet && acceptancePaymentSet.status === AcceptancePaymentSetStatus.PENDING && (
                <>
                  <PageHeaderAction
                    label="Pay Tips"
                    data-cy="tip-acceptance-pay-tips-cta"
                    to={`${routeConstants.private.makePayment}/acceptance/${currentSite.id}/${payment_set_id}`}
                    disabled={acceptanceBalance <= 0}
                    tooltipId={`${acceptancePaymentSet.id}_pay_disabled`}
                  />
                  <PayTipsTooltip id={`${acceptancePaymentSet.id}_pay_disabled`} balance={acceptanceBalance} />
                </>
              )}
            </PageActions>
          )}
        </PageHeaderContent>

        {hasStats && <CardRow />}
      </PageHeader>

      {children}
    </>
  );
};

export default TipAcceptanceScreenLayout;
